import React, { useCallback, useEffect, useState } from 'react';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useRegisterPageContainerError } from '../../../../components/layout/page-container/page-container-error-handler';
import LoadMoreFeedback from '../../../../components/reusable/load-more-feedback/LoadMoreFeedback';
import Search from '../../../../components/reusable/search/Search';
import UsersListBig from '../../../../components/reusable/users-list/UsersListBig';
import { UsersListRefreshProvider } from '../../../../components/reusable/users-list/UsersListRefreshContext';
import { useLazyLoad } from '../../../../hooks/use-lazy-load';
import { useDebounce } from '../../../../hooks/useDebounce';
import { useLazyGetUsers } from '../../../../redux/api/users/usersApi';
import { useStyles } from './AllUsers.styles';

export default function AllUsers() {
  const [filter, setFilter] = useState('');
  const [loaded, setLoaded] = useState(false);
  const {
    items: users,
    footerRef,
    resetAll,
    refetchAll,
    isFetching: isFetchingUsers,
    error: usersError,
  } = useLazyLoad(useLazyGetUsers, { searchValue: filter });

  const callback = useCallback(() => {
    resetAll(true);
  }, []);

  const [debouncedSearch] = useDebounce<(a: any) => void>(callback, 1000);

  const theme = useTheme();

  const { classes } = useStyles(theme);
  const handleRetry = useCallback(() => {
    resetAll(true);
  }, [resetAll]);

  useRegisterPageContainerError({
    errors: usersError ? [usersError] : [],
    isLoading: (!users || users.length === 0) && Boolean(isFetchingUsers),
    hasData: Boolean(users?.length),
    onRetry: handleRetry,
  });

  useEffect(() => {
    if (loaded) {
      debouncedSearch(filter);
    } else {
      setLoaded(true);
    }
  }, [filter]);

  return (
    <Box>
      <Box display="flex" alignItems="flex-end" gap={1} marginTop={1}>
        <Search
          fullWidth
          value={filter}
          onChange={(value) => {
            setFilter(value);
            debouncedSearch(value);
          }}
          className={classes.usersListSearch}
        />
      </Box>
      <UsersListRefreshProvider refetch={refetchAll}>
        <UsersListBig
          users={users}
          isLoading={(!users || users.length === 0) && Boolean(isFetchingUsers)}
          isError={Boolean(usersError) && users == null}
          listFooter={
            <>
              <LoadMoreFeedback
                error={usersError}
                isFetching={Boolean(isFetchingUsers) && Boolean(users?.length)}
                onRetry={() => resetAll(true)}
              />
              <Box ref={footerRef} mb={1} sx={{ minHeight: '1px' }} />
            </>
          }
        />
      </UsersListRefreshProvider>
    </Box>
  );
}
